import { ApiError, Config, Endpoint, Request } from '~/models/request';
import axios, { AxiosError } from 'axios';
import api from '~/utils/api';
import { toast } from '~/components/Toaster';

export const handleError = <Variables = unknown, Error = unknown, Options = unknown>(
	config: Config<Variables, Error> & Options
) => {
	return (error?: Error, variables?: Variables, context?: any) => {
		const errorMessage =
			config?.settings?.formatErrorMessage?.(
				(error as AxiosError)?.response?.data,
				(error as AxiosError<ApiError>)?.response?.data?.errorMessage ?? ''
			) ?? (error as AxiosError<ApiError>)?.response?.data?.errorMessage;

		if (
			((error as AxiosError)?.response?.status ?? 0) >= 400 &&
			((error as AxiosError)?.response?.status ?? 0) < 500 &&
			!!errorMessage
		) {
			toast.error(errorMessage);
		}
	};
};

export const formatEndpoint = (endpoint: Endpoint, params?: Config['params']) => {
	const suffix = params ? new URLSearchParams(params).toString() : '';
	const keySuffix = params ? ['?', ...suffix.split('&')] : [];

	if (typeof endpoint === 'object' && endpoint !== null)
		return {
			...endpoint,
			key:
				endpoint?.key ??
				endpoint?.path
					.split('/')
					.filter((x) => !!x)
					.concat(keySuffix),
		};

	if (typeof endpoint === 'string') {
		return {
			key: endpoint
				?.split('/')
				.filter((x) => !!x)
				.concat(keySuffix),
			path: endpoint,
		};
	}

	return null;
};

export const controller = typeof window !== undefined ? new AbortController() : undefined;

export const requestCancelled = (error: unknown) => axios.isCancel(error);

export const request = async <Data, Variables = unknown, Error = unknown>(
	request: Request<Variables, Error>
): Promise<Data> => {
	try {
		const endpoint = formatEndpoint(request.endpoint);
		// axios.defaults.withCredentials = true;

		if (!endpoint) throw undefined;

		const pattern = /^((http|https):\/\/)/;
		const type = request?.config?.settings?.type;

		const baseURL =
			request?.config?.settings?.baseURL ?? pattern.test(endpoint?.path) ? '' : api.baseURL;

		const params = {
			page: request.config.page,
			per_page: request.config.per_page,
			...(request.config.params ?? {}),
		};

		if (type === 'PUT' || type === 'POST') {
			const { data: d }: { data: Data } = await (type === 'PUT' ? axios.put : axios.post)(
				`${baseURL}${endpoint.path}`,
				request.config?.data || null,
				{
					...(request.config.requestConfig ?? {}),
					params: {
						...params,
						...(request.config.requestConfig?.params ?? {}),
					},
					timeout: 15000,
					signal: controller?.signal,
				}
			);

			return d;
		}

		const axiosRes: { data: Data } = await (type === 'DELETE' ? axios.delete : axios.get)(
			`${baseURL}${endpoint.path}`,
			{
				...(request.config.requestConfig ?? {}),
				params: {
					...params,
					...(request.config.requestConfig?.params ?? {}),
				},
				timeout: 15000,
				signal: controller?.signal,
			}
		);

		const d = axiosRes?.data;

		return d;
	} catch (_error: any) {
		const error = _error as AxiosError<ApiError>;

		if (requestCancelled(error)) throw new Error();

		throw error;
	}
};
